<template>
  <div v-if="loading" class="d-flex justify-content-center my-5 py-5">
    <div class="text-center">
      <b-spinner class="align-middle primary-color mr-2" />
      <strong class="primary-color">Loading inquiry…</strong>
    </div>
  </div>
  <div v-else-if="activeInquiry && activeInquiry.InquiryId === 0" class="d-flex justify-content-center">
    <b-card title="404" class="p-5">
      <b-card-text>Error - Inquiry not found</b-card-text>
    </b-card>
  </div>
  <div v-else>
      <b-alert show variant="info" class="text-center small">
          All pricing being provided during the inquiry are estimates.
          Final pricing will be provided once a Sales or Customer Service contacts you confirming your order.
          Inquiries not converted to an Order in 30 days will be canceled.
      </b-alert>
    <b-card>
      <!-- Row 1 -->
      <b-row class="align-items-center pb-3 my-1" align-v="center">
        <b-col cols="3" md="auto">
          <h5>
            <span
              v-if="_hasHistory()"
              class="mr-4 primary-color back-btn"
              @click="goBackToInquiryList"
            >
              <b-icon icon="arrow-left"></b-icon>
            </span>
            <i class="fa fa-truck fa-lg mr-2"></i>TPI Inquiry Summary
          </h5>
        </b-col>
        <b-col>
          <span class="rounded border p-2" style="background-color: #d6eaf8">
            <b>{{ activeInquiry.Status }}</b>
          </span>
          <span v-if="activeInquiry.CancelDate" class="text-danger p-2">
            <b>{{ formatDate(activeInquiry.CancelDate) }}</b> {{ activeInquiry.CancelReason }}
          </span>
        </b-col>
        <b-col cols="3" md="auto" style="text-align: right;">
          <b-button v-hideIf.cantWrite="{ module: this.permissionModule }"
            v-if="activeInquiry.Status !== 'Cancelled'"
            variant="outline-danger"
            class="mr-3"
            @click="openCancelInquiryModal"
          >
            <b class="ml-2">Cancel Inquiry</b>
          </b-button>
          <b-button
            v-if="['Open', 'Approved', 'Cancelled'].includes(activeInquiry.Status)"
            type="button"
            variant="outline-primary"
            class="mr-3"
            :disabled="inquiryPdfLoading"
            @click.prevent="downloadInquirySummaryPdf"
          >
            <b-spinner
              v-if="inquiryPdfLoading"
              small
              type="border"
              class="align-middle primary-color mr-1"
            />
            <b-icon v-else icon="file-earmark-pdf" class="mr-1"></b-icon>
            <b>Print Quote</b>
          </b-button>
          <b-button
            v-if="hasConfigurableMaterial && ['Open', 'Approved', 'Cancelled'].includes(activeInquiry.Status)"
            type="button"
            variant="outline-primary"
            class="mr-3"
            @click.prevent="openSubmittalPdf"
          >
            <b-icon icon="file-earmark-pdf" class="mr-1"></b-icon>
            <b>Print Submittal</b>
          </b-button>
          <b-button
            v-hideIf.cantWrite="{ module: this.permissionModule }"
            v-if="showUncancelInquiry"
            type="button"
            variant="outline-primary"
            class="mr-3"
            @click.prevent="() => showUncancelModal = true"
          >
            <b>Reopen</b>
          </b-button>
          <b-button
            v-if="activeInquiry.Status !== 'Cancelled'"
            variant="outline-primary"
            class="mr-3"
            @click="editInquiry"
          >
            <b class="ml">Edit</b>
          </b-button>
          <b-button
            v-hideIf.cantWrite="{ module: Modules.ConfigurableMaterial }"
            v-if="false &&activeInquiry.Status === 'Open'"
            variant="outline-primary"
            class="mr-3"
            @click="displayRequestModal"
          >
            <b class="ml">Request Special Multiplier</b>
          </b-button>
          <b-button
            v-hideIf.cantExecute="{ module: Modules.ConfigurableMaterial }"
            v-if="hasConfigurableMaterial && _isCustomerAdminSite()"
            variant="outline-primary"
            class="mr-3"
            @click="displayApproveModal"
          >
            <b class="ml">Modify Prices & Multipliers</b>
          </b-button>
          <b-button
            v-if="!isCustomerPortalSite() && (activeInquiry.Status === 'Approved' || activeInquiry.Status === 'Open')"
            variant="info"
            class="background-color-primary"
            @click="convertOrder"
          >
            <b class="ml-2">Convert to Order</b>
          </b-button>
          <b-modal
            v-model="showCancelModal"
            title="Cancel Inquiry"
            size="md"
            centered
            @hidden="onCancelInquiryModalHidden"
          >
            Please enter a note for cancelling the order.
            <b-textarea v-model="cancelReason" autocomplete="off"></b-textarea>
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="closeCancelInquiryModal"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="danger"
                @click="cancel"
              >
                <b>Ok</b>
              </b-button>
            </template>
          </b-modal>
          <b-modal v-model="showUncancelModal" title="Reopen" size="md" centered>
            <p v-if="uncancelShowsPricingReopenNotice" class="mb-0">
              A special pricing has been applied to this Inquiry. Please click the button below to reopen it for approval.
            </p>
            <p v-else class="mb-0">
              This will restore the inquiry to its Open status.
            </p>
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="() => showUncancelModal = false"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="outline-primary"
                @click="confirmUncancel"
              >
                <b>{{ uncancelShowsPricingReopenNotice ? 'Reopen' : 'Ok' }}</b>
              </b-button>
            </template>
          </b-modal>
          <b-modal v-model="showEditAggreeModal" title="Edit Inquiry" size="md" centered>
            If you edit the configurable material your special pricing will be reset.
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="() => showEditAggreeModal = false"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="primary"
                @click="editInquiryAgree"
              >
                <b>Ok</b>
              </b-button>
            </template>
          </b-modal>
          <b-modal v-model="showApproveModal" title="Modify Prices & Multipliers"
            content-class="config-modal-body" size="xlg" centered>
            <div class="row">
              <div class="col">
                <div class="row">
                  <div class="col-md-3"><b>Issued By:</b></div><div class="col-md-8">{{ activeInquiry.RequestUser }}</div>
                  <div class="col-md-3"><b>Request Note:</b></div><div class="col-md-8">{{ activeInquiry.RequestNote }}</div>
                </div>
              </div>
            </div>
            <table class="table table-sm">
              <thead>
                  <tr>
                    <th>Item Number</th>
                    <th>Catalog Number</th>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>List Price</th>
                    <th>Unit Multiplier</th>
                    <th style="width: 180px;">Special Multiplier</th>
                    <th style="width: 180px;">Net Price</th>
                    <th style="width: 180px;">Total</th>
                  </tr>
              </thead>
              <tbody v-if="showApproveModal">
                <template v-for="(parentItem, parentIndex) in activeInquiry.Items">
                  <tr v-if="parentItem.SubItems && parentItem.SubItems.length > 0" :key="'parent'+parentIndex" class="bg-light tree-parent">
                    <td colspan="6">
                      📦 Configurable Material: {{ parentItem.MaterialNumber }}
                      <button v-if="_isCustomerAdminSite()" class="btn btn-sm btn-outline-primary ml-3" @click="openSpecSheet(parentItem.ItemNumber)"><b-icon icon="file-earmark-text"></b-icon> View Spec Sheet</button>
                    </td>
                    <td colspan="3">
                        <b-button variant="outline-primary" size="sm"
                            @click="editAllMultiplier(parentIndex)"
                          ><b-icon icon="pencil"></b-icon><b-icon icon="arrow-down"></b-icon>
                          Update All
                        </b-button>
                    </td>

                  </tr>
                  <template v-for="(item) in getChildItems(parentItem)">
                    <template v-for="priceItem in [getCustomPrice(item.ItemNumber)]">
                      <tr :key="item.ItemNumber + priceItem.ItemNumber" :class="parentItem.SubItems && parentItem.SubItems.length > 0 ? 'tree-child text-muted' : ''">
                        <td>{{ item.ItemNumber }}</td>
                        <td>{{ item.MaterialNumber }}</td>
                        <td>{{ item.Description }}</td>
                        <td>{{ item.Quantity }}</td>
                        <td>
                            <!---->
                            <p>{{ formatPrice(priceItem.UnitPrice) }}</p>
                        </td>
                        <td>
                            <!---->
                            <p v-if="(parentItem.SubItems && parentItem.SubItems.length > 0)">{{ priceItem.Multiplier }}%</p>
                        </td>
                        <td>
                          <template v-if="!priceItem.editPrice && parentItem.SubItems && parentItem.SubItems.length > 0">
                            <b-button variant="outline-primary" size="sm" v-if="!priceItem.editMulti"
                              @click="editMultiplier(priceItem)"
                            ><b-icon icon="pencil"></b-icon></b-button>
                            <b-input-group size="sm" v-if="priceItem.editMulti">
                              <b-form-input type="number" number v-model="priceItem.sMultiplier" @input="multiplierChanged(priceItem)" step="0.1"></b-form-input>
                              <b-input-group-append>
                                <b-button variant="outline-danger" @click="cancelEditMultiplier(priceItem)"><b-icon icon="x"></b-icon></b-button>
                              </b-input-group-append>
                            </b-input-group>
                          </template>
                        </td>
                        <td>
                          <template v-if="!priceItem.editPrice">
                            {{ formatPrice(priceItem.sUnitPrice) }}
                            <b-button variant="outline-primary" size="sm" v-if="!priceItem.editMulti && parentItem.SubItems && parentItem.SubItems.length > 0 && priceItem.HasPrice"
                                      @click="priceItem.editPrice=true"><b-icon icon="pencil"></b-icon></b-button>
                          </template>
                          <b-input-group size="sm" v-if="priceItem.editPrice">
                            <b-form-input type="number" number v-model="priceItem.sUnitPrice" @input="unitPriceChanged(priceItem)" step="0.1"></b-form-input>
                            <b-input-group-append v-if="!priceItem.editMulti">
                              <b-button variant="outline-danger" @click="cancelEditPrice(priceItem)"><b-icon icon="x"></b-icon></b-button>
                            </b-input-group-append>
                          </b-input-group>
                        </td>
                        <td>
                          {{ formatPrice(getPriceItemTotalPrice(priceItem)) }}
                        </td>
                      </tr>

                      <template v-if="Object.keys(priceItem.Conditions).length > 0">
                        <template v-for="condition in Object.keys(priceItem.Conditions)">
                          <tr :key="item.ItemNumber + priceItem.ItemNumber + condition + 'conds'">
                            <td colspan="7"></td>
                            <td :key="condition.ConditionType + condition">
                              {{ condition }}
                            </td>
                            <td class="border-top">
                              <b-form-input type="number" number v-model="priceItem.Conditions[condition]" step="0.1" @input="multiplierChanged(priceItem)"></b-form-input>
                            </td>
                          </tr>
                        </template>
                      </template>
                    </template>
                  </template>
                  <tr v-if="parentItem.SubItems && parentItem.SubItems.length > 0" :key="'footer'+parentIndex">
                    <td colspan="8" class="text-right border-0 text-muted small">
                      Total:
                    </td>
                    <td class="border-top">
                      <b>{{ formatPrice(calculateSubTotal(parentItem, parentIndex)) }}</b>
                    </td>

                  </tr>
                </template>
              </tbody>
            </table>
            <div class="row">
              <div class="col text-right p-3">
                Order Total: <b>{{ formatPrice(calculateTotal) }}</b>
              </div>
            </div>
            <div class="row" v-if="false">
              <div class="col">
                <div class="row">
                  <div class="col-md-3"><b>Issued By:</b></div><div class="col-md-8">Zubeyr Aydemir</div>
                  <div class="col-md-3"><b>Request Note:</b></div><div class="col-md-8">Special pricing request notes...</div>
                </div>
                <div class="row">
                  <div class="col-md-3"><b>Customer:</b></div><div class="col-md-8">HOFFMAN & HOFFMAN/ROANOKE</div>
                  <div class="col-md-3"><b>Last Order Date:</b></div><div class="col-md-8">21-09-2024</div>
                </div>
                <div class="row mt-3" id="stats-container">
                    <!-- Dynamic stats will load here -->
                </div>
              </div>
              <div class="col">
                <div class="row">
                    <div class="col-12">
                        <div class="card">
                            <div class="card-body">
                                <h5 class="card-title mb-4">Totals</h5>
                                <div class="chart-container">
                                    <canvas id="timeSeriesChart"></canvas>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            </div>
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="() => showApproveModal = false"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="primary"
                @click="approvePricing"
              >
                <b>Approve</b>
              </b-button>
            </template>
          </b-modal>
          <b-modal v-model="showRequestModal" title="Add a note for special pricing" size="md" centered>
            <b-textarea v-model="pricingNote"></b-textarea>
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="() => showRequestModal = false"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="primary"
                @click="requestPricing"
              >
                <b>Ok</b>
              </b-button>
            </template>
          </b-modal>
          <b-modal v-model="showUpdateAllModal" title="Update all Multiplier" size="sm" centered>
            <b-row>
              <b-col>Total Price:</b-col>
              <b-col>{{ formatPrice(calculateSubTotal(activeInquiry.Items[currentUpdateAllIndex], currentUpdateAllIndex)) }}</b-col>
            </b-row>
            <b-row>
              <b-col>Current Multiplier:</b-col>
              <b-col>{{ getCurrentMultiplier(activeInquiry.Items[currentUpdateAllIndex]) }}%</b-col>
            </b-row>
            <b-row>
              <b-col>New Multiplier:</b-col>
              <b-col><b-input v-model="updateAllMultiplierValue" number @keydown.enter.native="updateAllMultiplier" autofocus onfocus="this.select()"></b-input></b-col>
            </b-row>
            <b-row>
              <b-col>New Price:</b-col>
              <b-col>{{ formatPrice(calculateSubTotalWithNewMultiplier(activeInquiry.Items[currentUpdateAllIndex], currentUpdateAllIndex)) }}</b-col>
            </b-row>
            <template #modal-footer>
              <b-button
                variant="secondary"
                @click="() => showUpdateAllModal = false"
              >
                <b>Close</b>
              </b-button>
              <b-button
                variant="primary"
                @click="updateAllMultiplier"
              >
                <b>Ok</b>
              </b-button>
            </template>
          </b-modal>
        </b-col>
      </b-row>

      <!-- Row 2 -->
      <b-row
        class="justify-content-between align-items-center p-1 border-y"
        style="
          background-color: #e7f0f8;
          border-color: #a6c2ef;
          margin-left: -1.25rem;
          margin-right: -1.25rem;
        "
      >
        <b-col><b>Inquiry Details</b></b-col>
      </b-row>

      <!-- Row 4 -->
      <b-row class="my-3">
        <b-col md="4" class="info-section" style="border-color: #a6c2ef">
          <b-col class="info-section-header" sm="border-top">Information</b-col>
          <b-row class="my-2 border-top pt-2">
            <b-col cols="6">Inquiry Number:</b-col>
            <b-col cols="6"
              ><u
                ><b>{{ activeInquiry.InquiryId }}</b></u
              ></b-col
            >
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Inquiry Date:</b-col>
            <b-col cols="6">{{ formatDate(activeInquiry.InquiryDate) }}</b-col>
          </b-row>
          <b-row class="my-2" v-if="activeInquiry.SalesOrderNumber">
            <b-col cols="6">Sales Order Number:</b-col>
            <b-col cols="6">
              <router-link
                :to="{
                  name: 'order_summary',
                  params: { invoiceNo: activeInquiry.SalesOrderNumber },
                }"
                class="order-link"
              >
                {{ activeInquiry.SalesOrderNumber }}
              </router-link>
            </b-col>
          </b-row>
          <template v-if="activeInquiry.Contact">
            <b-row class="my-2">
              <b-col cols="6">Job Name:</b-col>
              <b-col cols="6">{{ activeInquiry.JobName }}</b-col>
            </b-row>
            <b-row class="my-2" v-if="activeInquiry.Contact.Name">
              <b-col cols="6">Contact Name:</b-col>
              <b-col cols="6">{{ activeInquiry.Contact.Name }}</b-col>
            </b-row>
            <b-row class="my-2" v-if="activeInquiry.Contact.Phone">
              <b-col cols="6">Contact Phone:</b-col>
              <b-col cols="6">{{ activeInquiry.Contact.Phone }}</b-col>
            </b-row>
            <b-row class="my-2" v-if="activeInquiry.Contact.Email">
              <b-col cols="6">Contact Email:</b-col>
              <b-col cols="6"><a :href="'mailto:' + activeInquiry.Contact.Email">{{ activeInquiry.Contact.Email }}</a></b-col>
            </b-row>
          </template>
        </b-col>

        <b-col md="2" class="info-section" style="border-color: #a6c2ef">
          <b-col class="info-section-header">Value</b-col>
          <b-row class="my-2 border-top pt-2">
            <b-col cols="6">Subtotal :</b-col>
            <b-col cols="6" class="text-right" lg="text-right">{{
              formatCost(activeInquiry.Total)
            }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Sales Tax :</b-col>
            <b-col cols="6" class="text-right">{{
              formatCost(activeInquiry.Tax || 0)
            }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">{{ 'Estimated Total' }}:</b-col>
            <b-col cols="6 font-weight-bold" class="text-right">{{
              formatCost(grandTotal)
            }}</b-col>
          </b-row>
        </b-col>

        <b-col
          md="3"
          class="info-section"
          style="border-color: #a6c2ef"
          v-if="activeInquiry.BillToParty"
        >
          <b-col class="info-section-header">Bill To</b-col>
          <b-row class="my-2 border-top pt-2">
            <b-col cols="6">Party :</b-col>
            <b-col cols="6">{{ activeInquiry.BillToParty.Name }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Phone # :</b-col>
            <b-col cols="6">{{ activeInquiry.BillToParty.Phone }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Address :</b-col>
            <b-col cols="6">{{
              `${activeInquiry.BillToParty.Address1}, ${activeInquiry.BillToParty.Address2}
              ${activeInquiry.BillToParty.City}, ${activeInquiry.BillToParty.State} ${activeInquiry.BillToParty.Zip}
              `
            }}</b-col>
          </b-row>
        </b-col>

        <b-col md="3" class="info-section" v-if="activeInquiry.ShipToParty">
          <b-col class="info-section-header">Ship To</b-col>
          <b-row class="my-2 border-top pt-2">
            <b-col cols="6">Party :</b-col>
            <b-col cols="6">{{ activeInquiry.ShipToParty.Name }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Phone # :</b-col>
            <b-col cols="6">{{ activeInquiry.ShipToParty.Phone }}</b-col>
          </b-row>
          <b-row class="my-2">
            <b-col cols="6">Address :</b-col>
            <b-col cols="6">{{
              `${activeInquiry.ShipToParty.Address1}, ${activeInquiry.ShipToParty.Address2}
              ${activeInquiry.ShipToParty.City}, ${activeInquiry.ShipToParty.State} ${activeInquiry.ShipToParty.Zip}
              `
            }}</b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-tabs align="left">
        <b-tab title="Items">
          <template #title>
            Items (<b>{{ totalSelectedQty }}</b>)
          </template>
          <div style="margin: -1.25rem">
            <inquiry-table
              class="my-3"
              :fields="fields"
              :items="activeInquiry.Items"
              :disableInput="true"
              :displaySpecSheet="_isCustomerAdminSite()"
              @openSpecSheet="openSpecSheet"
            />
            <template v-if="inquiryConfigurations && Object.keys(inquiryConfigurations).length > 0">
              <template v-for="item in activeInquiry.Items.filter(i => i.SubItems && i.SubItems.length > 0)">
                <configurable-material-specsheet :key="item.ItemNumber" :ref="'specSheetModal' + item.ItemNumber" :configurations="inquiryConfigurations[item.ItemNumber]"></configurable-material-specsheet>
              </template>
            </template>
          </div>
        </b-tab>
        <b-tab :title="`Comments (${activeInquiry.Comments.length})`" >
          <b-row class="my-2">
            <b-col cols="12" v-for="(comment, index) in activeInquiry.Comments" :key="index">
              <i>{{ comment }}</i>
            </b-col>
          </b-row>
        </b-tab>
        <b-tab title="Files" :disabled="documentsLoading" v-if="site.ManageDocuments">
          <template #title>
            Files <b v-if="!documentsLoading">({{ inquiryDocuments.length }})</b><b v-else><i class="fa fa-spinner fa-pulse"></i></b>
          </template>
          <div style="margin: -1.25rem">
            <div class="item-table-container my-3">
              <b-table
                :fields="fileFields"
                :items="inquiryDocuments"
                responsive
                hover
                borderless
                sticky-header
                class="item-table"
                style="min-height: 200px;"
              >
                <template v-slot:cell(Name)="{ item }">
                  <b-link :href="`/api/inquiry/document/${item.Id}/${item.Name}?h=${item.Hash}`" target="_blank">
                    {{ item.Name }}{{ item.FileExtension }}
                  </b-link>
                </template>
              </b-table>
              <b-button variant="primary" @click="() => { showOrderDocuments = true }" class="ml-3">
                Manage files
              </b-button>
              <b-modal v-model="showOrderDocuments" title="Inquiry Documents" size="xl" ok-only>
                <order-documents :orderDocuments="inquiryDocuments" :showFileUpload="true" :showClipUnclip="false" :showDelete="true"
                @upload="upload"
                @delete="deleteFile"
                ></order-documents>
                <template #modal-footer>&nbsp;</template>
              </b-modal>
            </div>
          </div>
        </b-tab>
      </b-tabs>
    </b-card>
    <div class="d-flex justify-content-center">
      <b-button
        block
        variant="outline-primary"
        class="primary-outline-btn my-3"
        @click="$router.push({ name: 'home' })"
      >
        Close summary
      </b-button>
    </div>
  </div>
</template>

<script lang="ts">
import InquiryTable from '@/components/table/inquiry-table.vue'
import LoadingText from '@/components/utility/loading.vue'
import { CartItem } from '@/store/cart/types'
import { IInquiry } from '@/store/inquiry/types'
import dayjs from 'dayjs'
import { Component, Mixins, Vue, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import MxSite from '@/mixins/site'
import { Modules, ResourceType } from '@/store/security/types'
import { isCustomerAdminSite, isCustomerSite } from '@/helpers/sites.helper'
import { IOrderDocument } from '../store/orders/types'
import OrderDocuments from '@/components/orders/order-documents.vue'
import ConfigurableMaterialSpecSheet from '../components/modals/configurable-material-specsheet.vue'

const nsInquiry = namespace('inquiries')

@Component({
  components: {
    InquiryTable,
    LoadingText,
    'order-documents': OrderDocuments,
    'configurable-material-specsheet': ConfigurableMaterialSpecSheet,
  },
})
export default class InquirySummary extends Mixins(MxSite) {
  @nsInquiry.State('activeInquiry')
  public activeInquiry!: IInquiry

  @nsInquiry.State('documentsLoading')
  public documentsLoading!: boolean

  @nsInquiry.State('submissionError')
  public submissionError!: string

  @nsInquiry.State('inquiryDocuments')
  public inquiryDocuments!: IOrderDocument[]

  @nsInquiry.Action('uploadDocuments')
  public uploadDocuments: any

  @nsInquiry.Action('deleteDocuments')
  public deleteDocuments: any

  @nsInquiry.Action('fetchDocuments')
  private fetchDocuments: any

  @nsInquiry.Action('fetchConfigurations')
  private fetchConfigurations: any

  @nsInquiry.Action('fetchInquiry')
  private fetchInquiry: any

  @nsInquiry.Action('cancelInquiry')
  private cancelInquiry: any

  @nsInquiry.Action('uncancelInquiry')
  private uncancelInquiry: any

  @nsInquiry.State('rememberedInquiryListTab')
  private rememberedInquiryListTab!: string

  @nsInquiry.Action('requestSpecialPricing')
  private requestSpecialPricing: any

  @nsInquiry.Action('approveSpecialPricing')
  private approveSpecialPricing: any

  public get permissionModule() {
    return this._isCustomerAdminSite() ? Modules.InquiryAdmin : Modules.OrderAdmin
  }

  public items: CartItem[] = []

  public showOrderDocuments = false

  public prices: any = []
  public currentUpdateAllIndex = 0
  public updateAllMultiplierValue = 0

  public get calculateTotal() {
    return this.prices.reduce((partialSum: any, a: any) => partialSum + a.TotalPrice, 0)
  }

  public getPriceItemTotalPrice(priceItem: any) {
    return priceItem.TotalPrice
  }

  public calculateSubTotal(item: any) {
    const itemNumbers = item.SubItems?.map((s: any) => s.ItemNumber) ?? [item.ItemNumber]
    return this.prices.filter(s => itemNumbers.includes(s.ItemNumber)).reduce((partialSum: any, a: any) => partialSum + a.TotalPrice, 0)
  }

  private getConditionPrices(item: any) {
    const vals = Object.values(item.Conditions)
    if (vals.length >= 0) {
      const conditions = vals.map((s: any) => parseFloat(s)).filter((s: any) => !isNaN(s))
      return conditions.reduce((partialSum: any, a: any) => partialSum + a, 0)
    }
    return 0
  }

  public calculateSubTotalWithNewMultiplier(item: any, index: number) {
    return this.prices.slice(index, item.SubItems?.length ?? 1).reduce((partialSum: any, a: any) => partialSum + this.calculateMultiplierPrice(a.UnitPrice * 1, this.updateAllMultiplierValue * 1), 0)
  }

  public getCurrentMultiplier(item: any) {
    const items = item.SubItems.filter(s => parseFloat(s.Multiplier) !== 0).map(s => parseFloat(s.Multiplier))
    if (items.length === 0) {
      items.push(0)
    }
    let multi = Math.max(...(items))
    if (multi < 0) {
      multi = 100 + multi
    }
    return multi
  }

  public getCustomPrice(itemNumber: any) {
    return this.prices.filter(s => s.ItemNumber === itemNumber)[0]
  }

  public calculateMultiplierPrice(unitPrice: number, multiplier: number) {
    let total = unitPrice
    if (multiplier < 0) {
      multiplier = 100 + multiplier
    }
    total = (total * multiplier / 100)
    return total
  }

  public updateAllMultiplier() {
    for (let index = 0; index < this.activeInquiry.Items[this.currentUpdateAllIndex].SubItems.length; index++) {
      const element = this.activeInquiry.Items[this.currentUpdateAllIndex].SubItems[index]
      const price = this.getCustomPrice(element.ItemNumber)
      price.sMultiplier = this.updateAllMultiplierValue
      price.editMulti = true
      price.editPrice = false
      this.multiplierChanged(price)
    }
    this.showUpdateAllModal = false
  }

  public editMultiplier(price) {
    price.editMulti = true
  }

  public cancelEditMultiplier(price) {
    price.sMultiplier = price.Multiplier
    price.editMulti = false
    this.multiplierChanged(price)
  }

  public cancelEditPrice(price) {
    price.sUnitPrice = price.UnitPrice
    price.editPrice = false
    this.unitPriceChanged(price)
  }

  public multiplierChanged(price) {
    price.sUnitPrice = this.calculateMultiplierPrice(price.UnitPrice + this.getConditionPrices(price), price.sMultiplier)
    this.unitPriceChanged(price)
  }

  public unitPriceChanged(price) {
    price.TotalPrice = price.sUnitPrice * price.Quantity
  }

  public get isMobile() {
    return window.innerWidth <= 1024
  }

  public fields = [
    {
      key: 'details',
      label: 'Toggle Details',
      thClass: 'responsive-col-hide text-left',
      tdClass: 'responsive-col-hide text-left',
      thStyle: { width: '10%' },
    },
    {
      key: 'imgThumbnail',
      label: '',
      thClass: 'responsive-col text-left',
      tdClass: 'responsive-col text-left',
      thStyle: { width: '5%' },
    },
    {
      key: 'ItemNumber',
      label: 'Item Number',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
    },
    {
      key: 'MaterialNumber',
      label: 'PCN',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
    },
    {
      key: 'Description',
      label: 'Description',
      thClass: 'responsive-col text-left',
      tdClass: 'responsive-col text-left',
      thStyle: { width: '20%' },
    },
    {
      key: 'Price',
      label: 'Unit Price',
      thClass: 'responsive-col text-right',
      tdClass: 'responsive-col text-right',
      thStyle: { width: '10%' },
      formatter: (val: any) => {
        if (val == null) {
          return ''
        } else if (isNaN(val)) {
          return val
        } else {
          return `$${parseFloat(val).toFixed(2)}`
        }
      },
    },
    {
      key: 'Quantity',
      label: 'Qty',
      thClass: 'responsive-col text-right',
      tdClass: 'responsive-col text-right',
      thStyle: { width: '10%', minWidth: '80px' },
    },
    {
      key: 'TotalPrice',
      label: 'Total Price',
      thClass: 'text-right',
      tdClass: 'responsive-col-size text-right',
      thStyle: 'resp-th-20',
      formatter: (val: any) => {
        if (val == null) {
          return ''
        } else if (isNaN(val)) {
          return val
        } else {
          return `$${parseFloat(val).toFixed(2)}`
        }
      },
    },
  ]

  public fileFields = [
    {
      key: 'Name',
      label: 'Name',
      thClass: 'responsive-col-size text-left',
      tdClass: 'responsive-col-size text-left',
    },
    {
      key: 'CreateDate',
      label: 'CreateDate',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      formatter: (val: any) => {
        return this.formatDate(val)
      },
    },
    {
      key: 'Size',
      label: 'Size (kb)',
      thClass: 'text-left',
      tdClass: 'responsive-col-size text-left',
      thStyle: 'resp-th-30',
      formatter: (val: any) => {
        return (val / 1024).toFixed(0)
      },
    },
  ]

  public selectAll = false

  public loading = false

  public inquiryPdfLoading = false
  public inquirySubmittalLoading = false

  public showCancelModal = false
  public showUncancelModal = false
  public cancelReason = ''
  public showRequestModal = false
  public showUpdateAllModal = false
  public showApproveModal = false
  public showEditAggreeModal = false
  public pricingNote = ''

  public get grandTotal() {
    return this.activeInquiry.Total + (this.activeInquiry.Tax || 0.0)
  }

  public get showUncancelInquiry(): boolean {
    return this.activeInquiry?.Status === 'Cancelled'
  }

  public get uncancelShowsPricingReopenNotice(): boolean {
    const inv = this.activeInquiry
    const d = inv?.ApproveDate ?? inv?.approveDate
    return d != null && d !== ''
  }

  public _isCustomerAdminSite() {
    return isCustomerAdminSite(this.role)
  }

  public isCustomerPortalSite() {
    return isCustomerSite(this.site.Name)
  }

  async created() {
    const scriti = document.createElement('script')
    scriti.setAttribute('src', 'https://cdn.jsdelivr.net/npm/chart.js')
    document.head.appendChild(scriti)

    window.scrollTo({
      top: 0,
      behavior: 'auto', // Optional: Add smooth scrolling animation
    })
    this.loading = true
    Vue.set(this, 'prices', [])

    const routeInquiryId = this.$route.params.inquiryId
    try {
      this.setOverlayShowing(true)
      await this.fetchInquiry(routeInquiryId)
      if (this.activeInquiry?.InquiryId) {
        let itemNumber = 1
        for (const itemIndex in this.activeInquiry.Items) {
          const item = this.activeInquiry.Items[itemIndex]
          if (item.SubItems && item.SubItems.length > 0) {
            for (const subItemIndex in item.SubItems) {
              const subItem = item.SubItems[subItemIndex]
              subItem.ItemNumber = this.itemNumberText(itemNumber++)
              const multiplier = subItem.Multiplier * 1 < 0 ? 100 + subItem.Multiplier * 1 : subItem.Multiplier
              const sMultiplier = subItem.SpecialMultiplier ?? multiplier
              const listItem: any = { ItemNumber: subItem.ItemNumber, Conditions: {}, UnitPrice: subItem.Price, HasPrice: subItem.HasPrice, Quantity: subItem.Quantity, Multiplier: multiplier, sUnitPrice: subItem.TotalPrice / subItem.Quantity, TotalPrice: subItem.TotalPrice, sMultiplier: sMultiplier, editMulti: multiplier !== sMultiplier, editPrice: false }
              if (subItem.Conditions && subItem.Conditions.length > 0) {
                for (const condition of subItem.Conditions.filter(s => s.VariantCondition === 'MISC_SURCHARGE' || s.VariantCondition === 'NEMA4')) {
                  listItem.Conditions[condition.VariantCondition] = condition.ConditionAmount
                }
              }
              this.prices.push(listItem)
            }
          } else {
            item.ItemNumber = this.itemNumberText(itemNumber++)
            const listItem: any = { ItemNumber: item.ItemNumber, Conditions: {}, UnitPrice: item.Price, Multiplier: item.Multiplier ?? 0, Quantity: item.Quantity, sUnitPrice: item.Price, TotalPrice: item.Price, sMultiplier: item.Multiplier, editMulti: false, editPrice: false }
            this.prices.push(listItem)
          }
        }
        this.fetchDocuments(routeInquiryId)
      }
      this.fetchConfigurations(this.activeInquiry.InquiryId).then((response: any) => {
        this.inquiryConfigurations = response
        this.setOverlayShowing(false)
      })
    } finally {
      this.loading = false
    }
  }

  public itemNumberText(number: number) {
    return (number * 10 + '').padStart(6, '0')
  }

  public get hasConfigurableMaterial(): boolean {
    return this.activeInquiry.Items.some(item => item.SubItems && item.SubItems.length > 0)
  }

  public get totalSelectedQty(): number {
    return this.activeInquiry.Items.reduce((total, item) => total + item.Quantity, 0)
  }

  public get totalSelectedPrice(): number {
    return this.activeInquiry.Items.reduce(
      (total, item) => total + item.Quantity * item.Price,
      0,
    )
  }

  public formatCost(cost: number): string {
    return `$${cost.toFixed(2)}`
  }

  public formatDate(date) {
    return dayjs(date).format('YYYY-MM-DD')
  }

  public goBackToInquiryList() {
    this.$router.push({ name: 'list-inquiry' })
  }

  public async convertOrder() {
    if (this.checkNemaBox()) {
      return
    }
    this.$router.push({ name: 'new-order', params: { fromInquiry: 'fromInquiry-' + this.activeInquiry.InquiryId } })
  }

  public openSubmittalPdf() {
    const submittalDoc = this.inquiryDocuments.filter(d => d.FileCategory === 'Submittal')[0]
    if (submittalDoc) {
      const url = `/api/inquiry/document/${submittalDoc.Id}/${submittalDoc.Name}?h=${submittalDoc.Hash}`
      window.open(url, '_blank')
    } else {
      this.toastInfo('Submittal form is being generated. Please refresh and check the files tab shortly for the download link. If you do not see the file within 1 minute, please contact support.')
    }
  }

  public checkNemaBox() {
    let hasNemaBox = false
    Object.values(this.inquiryConfigurations).forEach((config: any) => {
      if (this.activeInquiry.Status !== 'Approved' && config?.Values) {
        config.Values.forEach((value: any) => {
          if (value?.Characteristic === 'CH_HF_NEMA_BOX_1' || value?.Characteristic === 'CH_CHMS_NEMA_BOX_1') {
            hasNemaBox = true
          }
        })
      }
    })
    if (hasNemaBox) {
      this.toastInfo('Orders containing NEMA4 specifications cannot be created directly. Please email your TPI representative')
    }
    return hasNemaBox
  }

  public async downloadInquirySummaryPdf() {
    if (this.checkNemaBox()) {
      return
    }
    if (this.inquiryPdfLoading) {
      return
    }
    const inquiryId = this.activeInquiry?.InquiryId
    if (!inquiryId) {
      this.toastError('Inquiry is not loaded yet.')
      return
    }
    const apiPrefix = (this.$store.state as any).apiPrefix ?? ''
    // Must include /api: raw fetch does not use BaseApi.baseUrl; devServer proxy only matches ^/api (see config/dev.js).
    const url = `/api${apiPrefix}/inquiry/pdf/${inquiryId}`
    const security = (this.$store.state as any).security
    const user = security?.currentUser
    const headers: Record<string, string> = {}
    if (user?.BearerToken) {
      headers.Authorization = `Bearer ${user.BearerToken}`
    } else if (user?.SecurityToken) {
      headers['X-Security-Token'] = user.SecurityToken
    }
    this.inquiryPdfLoading = true
    await this.$nextTick()
    let objectUrl: string | null = null
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers,
        credentials: 'same-origin',
        cache: 'no-store',
        redirect: 'manual',
      })
      if (response.type === 'opaqueredirect' || (response.status >= 300 && response.status < 400)) {
        this.toastError('Could not download print file. Please sign in again and try once more.')
        return
      }
      if (!response.ok) {
        this.toastError(
          response.status === 404
            ? 'Print file could not be generated (template or inquiry missing).'
            : `Could not download print file (${response.status}).`,
        )
        return
      }
      const blob = await response.blob()
      const contentType = (response.headers.get('content-type') ?? '').toLowerCase()
      if (contentType.includes('text/html') || contentType.includes('application/json')) {
        this.toastError('Server did not return a print file. Try again or contact support.')
        return
      }
      const filename =
        this.parseFilenameFromContentDisposition(response.headers.get('content-disposition')) ??
        `InquirySummary-${inquiryId}.pdf`
      objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl)
        }
      }, 2500)
      this.toastSuccess('The inquiry summary print file was created and downloaded.')
    } catch {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
      this.toastError('Could not download print file. Check your connection and try again.')
    } finally {
      this.inquiryPdfLoading = false
    }
  }

  private parseFilenameFromContentDisposition(header: string | null): string | null {
    if (!header) {
      return null
    }
    const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header)
    if (utf8?.[1]) {
      try {
        return decodeURIComponent(utf8[1].replace(/(^")|("$)/g, ''))
      } catch {
        return utf8[1]
      }
    }
    const quoted = /filename="([^"]+)"/i.exec(header)
    if (quoted?.[1]) {
      return quoted[1]
    }
    const plain = /filename=([^;\s]+)/i.exec(header)
    return plain?.[1]?.replace(/^"|"$/g, '') ?? null
  }

  public async editInquiry() {
    if (this.activeInquiry.Status === 'Approved') {
      this.showEditAggreeModal = true
    } else {
      this.editInquiryAgree()
    }
  }

  public async editInquiryAgree() {
    this.$store.commit('inquiries/inquiryEditorOpenOnItemsTabSet', false)
    this.$router.push({ name: 'new-inquiry', params: { fromInquiry: 'fromInquiry-' + this.activeInquiry.InquiryId } })
  }

  public openSpecSheet(itemNumber: any) {
    const myModal: any = this.$refs['specSheetModal' + itemNumber]
    if (myModal) {
      myModal[0].open()
    }
  }

  public openCancelInquiryModal() {
    this.cancelReason = ''
    this.showCancelModal = true
  }

  public closeCancelInquiryModal() {
    this.showCancelModal = false
    this.cancelReason = ''
  }

  public onCancelInquiryModalHidden() {
    this.cancelReason = ''
  }

  public async cancel() {
    if (this.cancelReason && this.cancelReason.length > 0) {
      this.cancelInquiry({ inquiryId: this.activeInquiry.InquiryId, cancelReason: this.cancelReason })
      this.showCancelModal = false
      this.cancelReason = ''
    } else {
      alert('Cancel reason cannot be empty!')
    }
  }

  public async confirmUncancel() {
    this.showUncancelModal = false
    const ok = await this.uncancelInquiry({ inquiryId: this.activeInquiry.InquiryId })
    if (ok) {
      this.$store.commit('inquiries/inquiryEditorOpenOnItemsTabSet', true)
      this.$router.push({
        name: 'new-inquiry',
        params: { fromInquiry: 'fromInquiry-' + this.activeInquiry.InquiryId },
      })
    }
  }

  public get userSalesRepPermissions() {
    const permissions = (this.currentUser.ResourcePermissions ?? []).filter(item => item.Type === ResourceType.SalesRep)

    return permissions
  }

  public get customerPermissions() {
    const permissions = this.currentUser.ResourcePermissions.filter(item => item.Type === ResourceType.Customer)
    return permissions
  }

  public formatPrice(val: string | number): string {
    const num = typeof val === 'string' ? parseFloat(val) : val
    return isNaN(num) ? '$0.00' : '$' + num.toFixed(2)
  }

  @Watch('submissionError')
  public displayDocumentError() {
    if (this.submissionError) {
      this.toastError(this.submissionError)
    }
  }

  public upload(files: any) {
    this.showOrderDocuments = false
    this.uploadDocuments({ inquiryId: this.activeInquiry.InquiryId, files })
  }

  public deleteFile(documentId: any) {
    this.showOrderDocuments = false
    this.deleteDocuments({ inquiryId: this.activeInquiry.InquiryId, documentId })
  }

  public requestPricing() {
    this.requestSpecialPricing({ inquiryId: this.activeInquiry.InquiryId, requestNote: this.pricingNote }).then(() => {
      this.showRequestModal = false
    }).then(() => {
      this.toastSuccess('Your request has been sent.')
    })
  }

  public approvePricing() {
    const prices = this.prices.map(s => {
      const retObj = {
        'ItemNumber': s.ItemNumber,
        'Multiplier': null,
        'Price': null,
        'Conditions': [] as any[],
      }
      if (s.Conditions && Object.keys(s.Conditions).length > 0) {
        retObj.Conditions = Object.keys(s.Conditions).map(key => ({ Variant: key, Amount: s.Conditions[key] }))
      }
      if (s.Multiplier !== s.sMultiplier) {
        retObj.Multiplier = s.sMultiplier
      }
      return retObj
    })
    this.approveSpecialPricing({ inquiryId: this.activeInquiry.InquiryId, prices }).then(() => {
      this.showApproveModal = false
    }).then(() => {
      this.toastSuccess('Saved')
    })
  }

  public rawData = `2023,1,9759.42
2024,1,10510.54
2025,1,6057.80
2023,2,2289.82
2024,2,24266.87
2025,2,8188.73
2023,3,19915.42
2024,3,3755.22
2025,3,6487.80
2023,4,19103.14
2024,4,7504.46
2025,4,17243.07
2023,5,1447.32
2024,5,11112.36
2025,5,8917.24
2023,6,851.76
2024,6,21536.20
2025,6,26431.70
2024,7,29857.04
2025,7,13563.20
2023,8,5469.66
2024,8,5887.14
2022,9,54031.46
2023,9,1142.12
2024,9,15031.12`

  public monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

  public displayRequestModal() {
    this.showRequestModal = true
  }

  public inquiryConfigurations = []
  public displayApproveModal() {
    this.showApproveModal = true

    this.$nextTick(() => {
      // this.initDashboard()
    })
  }

  public editAllMultiplier(index) {
    this.showUpdateAllModal = true
    this.currentUpdateAllIndex = index
    this.updateAllMultiplierValue = this.getCurrentMultiplier(this.activeInquiry.Items[this.currentUpdateAllIndex])
  }

  public getChildItems(item) {
    if (item.SubItems && item.SubItems.length > 0) {
      return item.SubItems
    }
    return [item]
  }

  public initDashboard() {
    // 1. Parse and Sort Data Chronologically
    const parsedData = this.rawData.split('\n')
      .map(line => {
        const [y, m, v] = line.split(',').map(s => s.trim())
        return {
          year: parseInt(y),
          month: parseInt(m),
          value: parseFloat(v),
          timestamp: new Date(parseInt(y), parseInt(m) - 1).getTime(),
        }
      })
      .filter(d => !isNaN(d.year))
      .sort((a, b) => a.timestamp - b.timestamp)

    // 2. Prepare Chart Labels and Data
    const labels = parsedData.map(d => `${this.monthNames[d.month - 1]} ${d.year}`)
    const values = parsedData.map(d => d.value)

    // 3. Calculate Stats
    const total = values.reduce((a, b) => a + b, 0)
    const average = total / values.length
    const max = 4
    const totalOrder = 137
    const totalOrdera = 4
    const canceled = 4

    // 4. Update UI
    const statsContainer: any = document.getElementById('stats-container')
    const stats = [
      { label: 'Total Amount', value: `$${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-primary' },
      { label: 'Total Order Count', value: `${totalOrder.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-info' },
      { label: 'Active Years', value: `${max.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-info' },
      { label: 'Average Monthly Total', value: `$${average.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-primary' },
      { label: 'Average Monthly Orders', value: `${totalOrdera.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-success' },
      { label: 'Canceled Order Count', value: `${canceled.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, color: 'text-danger' },
    ]

    statsContainer.innerHTML = stats.map(s => `
        <div class="col-md-4 mt-3">
            <div class="card stat-card">
                <div class="card-body">
                    <div class="stat-label">${s.label}</div>
                    <div class="stat-value ${s.color}">${s.value}</div>
                </div>
            </div>
        </div>
    `).join('')

    // 5. Create Continuous Chart
    const qere: any = document.getElementById('timeSeriesChart')
    const ctx: any = qere.getContext('2d')
    // @ts-expect-error chart
    const asdf = new Chart(ctx, {
      type: 'line',
      data: {
        labels: labels,
        datasets: [{
          label: 'Total',
          data: values,
          borderColor: '#007bff',
          backgroundColor: 'rgba(0, 123, 255, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#007bff',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: true,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: value => '$' + value.toLocaleString(),
            },
          },
        },
        interaction: {
          intersect: false,
          mode: 'index',
        },
      },
    })
    console.log(asdf)
  }
}
</script>

<style lang="scss">
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 8px;
  background-color: #f4f7f9;
  position: sticky;
  top: 0;
  z-index: 10;

  .selected-info {
    display: flex;
    align-items: center;
    gap: 20px;

    .items {
      width: 15vh;
      text-align: center;
      border-right: 1px solid #a6c2ef;
    }
  }
  .actions {
    float: right;
  }
}

.back-btn {
  cursor: pointer;
}

.modal-dialog,
.config-modal-body {
    /* 80% of window height */
    height: 95%;
}
.modal-body {
    /* 100% = dialog height, 120px = header + footer */
    max-height: calc(100% - 120px);
    overflow-y: scroll;
}

.info-section {
  border-top: 1px solid #dee2e6 !important;
  padding-top: 0.5rem;
  margin-bottom: 1rem;
  @media (min-width: 767px) {
    border-right: 1px solid #dee2e6 !important;
    border-top: none !important;
    padding-top: 0;
    margin-bottom: 0;
  }
  .info-section-header {
    color: #7c97b6;
    font-weight: bold;
    font-size: small;
  }
}
        .table thead th { border-top: none; background: #f1f3f5; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.5px; vertical-align: middle; }

/* OPTION 2: The Indented Tree Line */
.tree-parent { font-weight: bold; color: #007bff; }
.tree-child td:first-child { position: relative; padding-left: 30px; }
.tree-child td:first-child::before {
    content: "";
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #dee2e6;
}
.tree-child td:first-child::after {
    content: "";
    position: absolute;
    left: 15px;
    top: 50%;
    width: 10px;
    height: 2px;
    background: #dee2e6;
}
.tree-child:last-of-type td:first-child::before { height: 50%; }

</style>
